import { type ApiError, useApi } from '@omnipresentgroup/api';
import { HTTPStatusCodes } from '@omnipresentgroup/constants';
import type {
  GetInvoiceReportEmployeeBillingBreakdownResponse,
  GetInvoiceReportEmployeeBillingBreakdownResponseAPI,
} from '@omnipresentgroup/types';
import { useQuery } from '@tanstack/react-query';

import {
  API_BASE,
  API_PATH,
  INVOICE_REPORT_EMPLOYEE_BREAKDOWN_API_PATH,
} from '../constants';
import { transformGetInvoiceReportEmployeeBreakdownResponseFromAPI } from '../mappers';

export const getInvoiceReportEmployeeBreakdownKey = (
  companyId: string,
  invoiceId: string,
  employeeId: string,
  showLocalCurrency?: boolean,
  invoiceIdToCompare?: string,
) => [
  API_PATH,
  companyId,
  'invoices',
  invoiceId,
  'employees',
  employeeId,
  'breakdown',
  'showLocalCurrency',
  showLocalCurrency,
  'compare',
  invoiceIdToCompare,
];

export const useInvoiceReportEmployeeBreakdown = ({
  companyId,
  invoiceId,
  employeeId,
  showLocalCurrency,
  invoiceIdToCompare,
}: {
  companyId: string;
  invoiceId: string;
  employeeId: string;
  showLocalCurrency?: boolean;
  invoiceIdToCompare?: string;
}) => {
  const { GET } = useApi();
  return useQuery<
    GetInvoiceReportEmployeeBillingBreakdownResponseAPI,
    ApiError,
    GetInvoiceReportEmployeeBillingBreakdownResponse
  >({
    queryKey: getInvoiceReportEmployeeBreakdownKey(
      companyId,
      invoiceId,
      employeeId,
      showLocalCurrency,
      invoiceIdToCompare,
    ),
    queryFn: () =>
      GET<GetInvoiceReportEmployeeBillingBreakdownResponseAPI>(
        `${API_BASE}/${INVOICE_REPORT_EMPLOYEE_BREAKDOWN_API_PATH(
          companyId,
          invoiceId,
          employeeId,
        )}`,
        {},
        {
          ...(invoiceIdToCompare && { compareWith: invoiceIdToCompare }),
          ...(showLocalCurrency && { localCurrency: `${showLocalCurrency}` }),
        },
      ),
    select: (data) =>
      transformGetInvoiceReportEmployeeBreakdownResponseFromAPI(data),
    enabled: !!companyId && !!invoiceId && !!employeeId,
    throwOnError: (error) => error.status !== HTTPStatusCodes.NOT_FOUND,
  });
};
